"use client";

import { useRef, useState, useEffect } from "react";
import classes from "./ProductPageButtons.module.css";
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";
import OfferModal from "../../../../../components/modals/product-related/OfferModal";
import MarkAsSoldModal from "../../../../../components/modals/product-related/MarkAsSoldModal";
import { useAlertContext } from "@/context/alert";
import { useCartContext } from "@/context/cart";
import { useAuthContext } from "@/context";
import AddedToCartModal from "../../../../../components/modals/product-related/AddedToCartModal";
import { trackEvent } from "@/hooks/TrackingFunctions";

const ProductPageButtons = ({
  listing,
  author,
  addressState,
  goShareEstimate,
  offered,
  uShipDeliveryEstimate,
  fedExEstimate,
  goShareReqBody,
  goShareReqEst,
  uShipReqEst,
  fedExEstimateObj,
}) => {
  const { REMOVE_FROM_CART, ADD_TO_CART } = useCartContext();
  const { SET_ALERT } = useAlertContext();
  const { cart } = useCartContext();
  const { auth } = useAuthContext();

  const isAuthor =
    auth.isAuthenticated && auth?.user?._id === listing?.author?._id;

  const [showCartSuccessModal, setShowCartSuccessModal] = useState(false);
  const [offer, setOffer] = useState(Math.ceil(listing.price * 0.9));
  const [markAsSoldModal, setMarkAsSoldModal] = useState(false);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [buyError, setBuyError] = useState("");
  const [stickyButtonsDiv, setStickyButtonsDiv] = useState(false);
  const [windowInnerWidth, setWindowInnerWidth] = useState();

  const sellButtonRef = useRef();
  const router = useRouter();

  useEffect(() => {
    setWindowInnerWidth(window.innerWidth);

    const handleScroll = () => {
      if (sellButtonRef.current) {
        setStickyButtonsDiv(
          window.scrollY >
            sellButtonRef.current.offsetTop + window.innerHeight - 100
        );
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleOffer = () => {
    const items = [
      {
        item_id: listing._id,
        item_name: listing.title,
        currency: "USD",
        item_brand: formatBrand(listing),
        item_category: listing.newSchema.categories[0],
        item_category2: listing.newSchema.subCategories[0] || "",
        item_category3: listing.newSchema.types[0] || "",
        price: listing.price,
      },
    ];

    trackEvent("begin_offer", {
      currency: "USD",
      value: listing.price * 0.2,
      items,
    });

    setShowOfferModal(true);
  };

  const handleBuy = (event) => {
    event.preventDefault();
    setBuyError("");

    if (listing.status !== "active") {
      SET_ALERT({ msg: "This listing is no longer available", type: "danger" });
      router.push(`/product/${listing.kashewId}/${formatTitle(listing.title)}`);
      return;
    }

    const items = [
      {
        item_id: listing._id,
        item_name: listing.title,
        currency: "USD",
        item_brand: formatBrand(listing),
        item_category: listing.newSchema.categories[0],
        item_category2: listing.newSchema.subCategories[0] || "",
        item_category3: listing.newSchema.types[0] || "",
        price: listing.price,
      },
    ];

    trackEvent("begin_checkout", {
      currency: "USD",
      value: listing.price * 0.2,
      items,
    });

    if (global?.window) {
      localStorage.setItem("offer", null);
      localStorage.setItem(
        "checkout",
        JSON.stringify([{ listingPrice: listing.price, id: listing._id }])
      );
    }

    setDeliveryDetails();
    router.push(`/checkout`);
  };

  const handleAddToCart = () => {
    if (cart.listings.find((i) => i._id === listing._id)) {
      REMOVE_FROM_CART(listing._id);

      trackEvent("remove_from_cart", {
        currency: "USD",
        value: listing.price * 0.2,
        items: [
          {
            item_id: listing._id,
            item_name: listing.title,
            currency: "USD",
            item_brand: formatBrand(listing),
            item_category: listing.newSchema.categories[0],
            item_category2: listing.newSchema.subCategories[0] || "",
            item_category3: listing.newSchema.types[0] || "",
            price: listing.price,
          },
        ],
      });
    } else {
      ADD_TO_CART(listing._id);
      setShowCartSuccessModal(true);

      trackEvent("add_to_cart", {
        currency: "USD",
        queryID: localStorage.getItem("queryID") || "",
        value: (listing.price * 0.2).toFixed(0),
        search_index: "kashew_listings",
        items: [
          {
            item_id: listing._id,
            item_name: listing.title,
            currency: "USD",
            item_brand: formatBrand(listing),
            item_category: listing.newSchema.categories[0],
            item_category2: listing.newSchema.subCategories[0] || "",
            item_category3: listing.newSchema.types[0] || "",
            price: listing.price,
          },
        ],
      });
    }
  };

  const setDeliveryDetails = () => {
    if (global?.window) {
      localStorage.setItem(
        "deliveryDetails",
        JSON.stringify({
          location: addressState,
          goSharePrice: goShareEstimate,
          ushipPrice: uShipDeliveryEstimate,
          fedExPrice: fedExEstimate,
          sellerPrice: offered,
          goShareReqBody,
          goShareReqEst,
          uShipReqEst,
          fedExEstimateObj,
          listingId: listing._id,
        })
      );
    }
  };

  const editHandler = (event) => {
    event.preventDefault();
    router.push(
      `/seller-dashboard?tab=products&selectedView=all&product=${listing._id}`
    );
  };

  const formatBrand = (listing) => {
    return (
      listing.brand ||
      (
        listing.velouAttributes?.manufacturer?.[0] ||
        listing.velouAttributes?.brands?.[0] ||
        ""
      )
        .replace(/_/g, " ")
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    );
  };

  const formatTitle = (title) => {
    return title
      .toLowerCase()
      .replace(/[_\W]+/g, "-")
      .replace(/^-+/, "")
      .replace(/-+$/, "");
  };

  const variants = {
    visible: { opacity: 1, y: 0 },
    hidden: { opacity: 0, y: "100%" },
  };

  return (
    <>
      {!isAuthor && listing.status === "active" && (
        <>
          {showOfferModal && (
            <OfferModal
              author={author}
              showOfferModal={showOfferModal}
              setShowOfferModal={setShowOfferModal}
              setOffer={setOffer}
              listing={listing}
              offer={offer}
              setDeliveryDetails={setDeliveryDetails}
            />
          )}

          <div ref={sellButtonRef} className={classes.buttonDiv}>
            {!listing.priceIsFirm && (
              <button className={classes.secondaryButton} onClick={handleOffer}>
                Make an Offer
              </button>
            )}
            <button className={classes.primaryButton} onClick={handleBuy}>
              Buy Now
            </button>
            <button
              className={
                listing.priceIsFirm
                  ? classes.secondaryButton
                  : classes.secondaryButtonSpan2
              }
              onClick={handleAddToCart}
            >
              {cart.listings.find((i) => i._id === listing._id)
                ? "Remove from cart"
                : "Add to Cart"}
            </button>
          </div>

          {windowInnerWidth < 600 && (
            <motion.div
              className={classes.buttonDivFixed}
              animate={stickyButtonsDiv ? "visible" : "hidden"}
              variants={variants}
            >
              {!listing.priceIsFirm && (
                <button
                  className={classes.secondaryButton}
                  onClick={handleOffer}
                >
                  Make an Offer
                </button>
              )}
              <button className={classes.primaryButton} onClick={handleBuy}>
                Buy Now
              </button>
              <button
                className={
                  listing.priceIsFirm
                    ? classes.secondaryButton
                    : classes.secondaryButtonSpan2
                }
                onClick={handleAddToCart}
              >
                {cart.listings.find((i) => i._id === listing._id)
                  ? "Remove from cart"
                  : "Add to Cart"}
              </button>
            </motion.div>
          )}
        </>
      )}
      {isAuthor && listing.status === "active" && (
        <>
          <div className={classes.buttonDiv}>
            <button
              className={classes.primaryButton}
              onClick={() => setMarkAsSoldModal(true)}
            >
              Mark As Sold
            </button>
            <button className={classes.secondaryButton} onClick={editHandler}>
              Edit Listing
            </button>
          </div>
        </>
      )}

      {showCartSuccessModal && (
        <AddedToCartModal
          setShow={setShowCartSuccessModal}
          show={showCartSuccessModal}
          cart={cart}
        />
      )}
      {markAsSoldModal && (
        <MarkAsSoldModal
          setMarkAsSoldModal={setMarkAsSoldModal}
          listing={listing}
        />
      )}
    </>
  );
};

export default ProductPageButtons;
